import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faExternalLinkAlt, faPlus, faTrash, faBuilding, faUser,
  faSpinner, faChevronDown, faChevronRight, faCalendarAlt, faClipboardList,
  faFileAlt, faCommentDots, faStickyNote, faEdit, faCertificate, faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import api from '../../../Api';
import { closingDateBadge } from '../closingDateBadge';
import { useModalDismiss } from '../../../hooks/useModalDismiss';
import PaymentRelanceCard from './PaymentRelanceCard';
import PaymentRelancePrompt from './PaymentRelancePrompt';

const STEP_LABELS = { ao: 'Avant-Vente', setup: 'Setup', terrain: 'Terrain', reporting: 'Reporting', closing: 'Closing' };
const STEP_COLORS = { ao: '#991b1b', setup: '#d97706', terrain: '#059669', reporting: '#0284c7', closing: '#7c3aed' };
const DEPT_STYLE = { define: { bg: '#e8f0fe', color: '#1967d2', label: 'Define' }, voa: { bg: '#fef7cd', color: '#a16207', label: 'Voa' }, immersion: { bg: '#fce8e6', color: '#c5221f', label: 'Immersion' } };
const TYPE_LABELS = { contrat: 'Contrat', appel_offre: 'Appel d\'Offre', consultation_restreinte: 'CR' };

function getStorageKey(projectId, channelKey) {
  return `project_${projectId}_read_${channelKey}`;
}

const ProjectQuickModal = ({ isOpen, onClose, project, onUpdate }) => {
  const navigate = useNavigate();
  const [checklists, setChecklists] = useState([]);
  const [checklistLoaded, setChecklistLoaded] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [localNotes, setLocalNotes] = useState('');
  const [localAttestation, setLocalAttestation] = useState(null);
  const [savingAttestation, setSavingAttestation] = useState(false);
  const [sendingAtt, setSendingAtt] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [localSentAt, setLocalSentAt] = useState(null);
  const [localRelanceCount, setLocalRelanceCount] = useState(0);
  const [relanceState, setRelanceState] = useState(null);
  const [relancePrompt, setRelancePrompt] = useState(false);

  // Changer de carte sans fermer la modale ne remonte pas le composant : sans cette
  // resynchronisation en rendu, la carte affiche le délai du projet précédent le temps
  // que l'effet d'ouverture passe.
  const [relanceProjectId, setRelanceProjectId] = useState(project?.id || null);
  if (relanceProjectId !== (project?.id || null)) {
    setRelanceProjectId(project?.id || null);
    setRelanceState(project?.payment_relance || null);
    setRelancePrompt(false);
  }

  const fetchChecklists = useCallback(async () => {
    if (!project?.id) return;
    try {
      const res = await api.get(`/projects/${project.id}/checklists`);
      setChecklists(res.data.data || []);
      setChecklistLoaded(true);
    } catch { setChecklistLoaded(true); }
  }, [project?.id]);

  // Fetch unread message count for the project channel (key=0)
  const fetchUnread = useCallback(async () => {
    if (!project?.id) return;
    try {
      const res = await api.get(`/projects/${project.id}/messages/latest-per-channel`);
      const latest = res.data.data || {};
      let count = 0;
      for (const [key, latestId] of Object.entries(latest)) {
        const stored = parseInt(localStorage.getItem(getStorageKey(project.id, key)) || '0', 10);
        if (latestId > stored) count++;
      }
      setUnreadCount(count);
    } catch { }
  }, [project?.id]);

  useEffect(() => {
    if (isOpen && project) {
      setChecklistLoaded(false);
      setUnreadCount(0);
      fetchChecklists();
      fetchUnread();
      setLocalNotes(project.notes || '');
      setEditingNotes(false);
      setNotesOpen(false);
      setLocalAttestation(project.attestation_requested ?? null);
      setLocalSentAt(project.attestation_request_sent_at || null);
      setLocalRelanceCount(project.attestation_relance_count || 0);
      setSendError(null);
      setRelanceState(project.payment_relance || null);
      setRelancePrompt(false);
    }
  }, [isOpen, project, fetchChecklists, fetchUnread]);

  // Holds inline edits (notes, checklists, assignees, attestation): a stray backdrop
  // click must not discard them. Exit via ✕, Escape, or the explicit buttons.
  const dismiss = useModalDismiss(onClose, { backdropClose: false, enabled: isOpen && !!project });
  // Destructive confirmation: require an explicit Annuler / ✕ / Escape.
  const deleteDismiss = useModalDismiss(() => setDeleteConfirm(null), { backdropClose: false, enabled: !!deleteConfirm });

  if (!isOpen || !project) return null;

  const handleAddChecklist = async () => {
    if (!newLabel.trim() || adding) return;
    setAdding(true);
    try {
      const res = await api.post(`/projects/${project.id}/checklists`, { step: project.step, label: newLabel.trim() });
      setChecklists(prev => [...prev, res.data.data]);
      setNewLabel('');
    } catch { }
    finally { setAdding(false); }
  };

  const handleToggle = async (itemId) => {
    setChecklists(prev => prev.map(i => i.id === itemId ? { ...i, is_completed: !i.is_completed, completed_by: !i.is_completed ? { name: 'Vous' } : null } : i));
    try {
      const res = await api.post(`/projects/${project.id}/checklists/${itemId}/toggle`);
      setChecklists(prev => prev.map(i => i.id === itemId ? res.data.data : i));
      applyClosingState(res.data.closing);
    }
    catch { fetchChecklists(); }
  };

  // Cocher « Facturation » fait basculer en attente de paiement : c'est le moment de demander
  // le délai accordé au client, tant qu'il est encore au défaut de 60 jours.
  const applyClosingState = (closing) => {
    if (!closing) return;
    setRelanceState(closing.payment_relance || null);
    if (closing.closing_step === 'attente_paiement' && closing.payment_relance?.is_default) {
      setRelancePrompt(true);
    }
  };

  const handleDelete = async (itemId) => {
    const backup = checklists;
    setChecklists(prev => prev.filter(i => i.id !== itemId));
    try { await api.delete(`/projects/${project.id}/checklists/${itemId}`); }
    catch { setChecklists(backup); }
    finally { setDeleteConfirm(null); }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/projects/${project.id}`, { notes: notesValue });
      setLocalNotes(notesValue);
      setEditingNotes(false);
      if (onUpdate) onUpdate();
    } catch { }
    finally { setSavingNotes(false); }
  };

  const handleSendAttestation = async () => {
    setSendingAtt(true); setSendError(null);
    try {
      const res = await api.post(`/projects/${project.id}/attestation-request`);
      setLocalSentAt(res.data.sent_at || new Date().toISOString());
      setLocalRelanceCount(res.data.relance_count ?? 0);
      if (onUpdate) onUpdate();
    } catch (err) {
      setSendError(err.response?.data?.message || 'Erreur lors de l\'envoi');
    } finally { setSendingAtt(false); }
  };

  const handleSetAttestation = async (requested) => {
    setSavingAttestation(true);
    try {
      await api.post(`/projects/${project.id}/attestation`, { requested });
      setLocalAttestation(requested);
      fetchChecklists();
      if (onUpdate) onUpdate();
    } catch { }
    finally { setSavingAttestation(false); }
  };

  const dept = DEPT_STYLE[project.department] || DEPT_STYLE.immersion;
  const stepColor = STEP_COLORS[project.step] || '#757575';
  const grouped = {};
  checklists.forEach(item => { const s = item.step || 'autre'; if (!grouped[s]) grouped[s] = []; grouped[s].push(item); });
  const doneCount = checklists.filter(i => i.is_completed).length;
  const attestationReceived = checklists.some(c => c.step === 'closing' && c.label === 'Attestation de référence' && c.is_completed);
  const total = project.total_slots || 0;
  const finished = project.finished_slots || 0;
  const pct = total > 0 ? Math.round((finished / total) * 100) : 0;
  const assignees = (project.assignees?.length > 0) ? project.assignees.map(a => a.name).join(', ') : project.assigned_to?.name || '';
  const docsCount = project.documents_count || 0;

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div style={overlay} {...dismiss}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: stepColor + '18', color: stepColor }}>
                {STEP_LABELS[project.step]}
              </span>
              <span style={{ fontSize: '0.6rem', fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: dept.bg, color: dept.color }}>{dept.label}</span>
              <span style={{ fontSize: '0.6rem', fontWeight: 500, padding: '2px 8px', borderRadius: 4, border: '1px solid #ededed', color: '#757575' }}>{TYPE_LABELS[project.project_type] || project.project_type}</span>
            </div>
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#242424', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {project.name}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, fontSize: '0.75rem', color: '#757575' }}>
              {project.client && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FontAwesomeIcon icon={faBuilding} size="xs" style={{ opacity: 0.4 }} /> {project.client.name}
                </span>
              )}
              {assignees && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FontAwesomeIcon icon={faUser} size="xs" style={{ opacity: 0.4 }} /> {assignees}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={closeBtn}><FontAwesomeIcon icon={faTimes} /></button>
        </div>

        <div style={{ padding: '0 24px 24px', overflowY: 'auto', flex: 1 }}>
          {/* Info row */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            {/* Progress */}
            <div style={{ ...infoCard, flex: 1 }}>
              <div style={infoCardLabel}>Enquêtes</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#242424' }}>{finished}<span style={{ color: '#bdbdbd', fontWeight: 400 }}>/{total}</span></span>
                {total > 0 && (
                  <div style={{ flex: 1, height: 4, background: '#f0f0f0', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? '#137333' : '#dc2626', borderRadius: 2, transition: 'width 0.4s' }} />
                  </div>
                )}
              </div>
            </div>
            {/* Dates */}
            <div style={{ ...infoCard, flex: 1 }}>
              <div style={infoCardLabel}>Période</div>
              <div style={{ display: 'flex', gap: 8, fontSize: '0.75rem' }}>
                <span style={{ color: project.date_debut ? '#242424' : '#d1d5db' }}>
                  <FontAwesomeIcon icon={faCalendarAlt} size="xs" style={{ opacity: 0.3, marginRight: 3 }} />{fmtDate(project.date_debut)}
                </span>
                <span style={{ color: '#d1d5db' }}>→</span>
                <span style={{ color: project.date_fin ? '#242424' : '#d1d5db' }}>{fmtDate(project.date_fin)}</span>
              </div>
            </div>
          </div>

          {/* Docs + Messages counters */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <div
              onClick={() => { onClose(); navigate(`/projects/${project.id}?tab=documents`); }}
              style={{ ...counterChip, cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f0f0f0'}
              onMouseLeave={e => e.currentTarget.style.background = '#fafafa'}
            >
              <FontAwesomeIcon icon={faFileAlt} size="sm" style={{ color: '#9e9e9e' }} />
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#424242' }}>{docsCount}</span>
              <span style={{ fontSize: '0.68rem', color: '#9e9e9e' }}>Documents</span>
            </div>
            <div
              onClick={() => { onClose(); navigate(`/projects/${project.id}?tab=brief`); }}
              style={{ ...counterChip, cursor: 'pointer', position: 'relative' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f0f0f0'}
              onMouseLeave={e => e.currentTarget.style.background = '#fafafa'}
            >
              <FontAwesomeIcon icon={faCommentDots} size="sm" style={{ color: '#9e9e9e' }} />
              <span style={{ fontSize: '0.68rem', color: '#9e9e9e' }}>Discussion</span>
              {unreadCount > 0 && (
                <span style={{
                  background: '#dc2626', color: '#fff', fontSize: '0.55rem', fontWeight: 700,
                  padding: '1px 6px', borderRadius: 10, marginLeft: 4,
                }}>
                  {unreadCount}
                </span>
              )}
            </div>
          </div>

          {/* Livrables deadline (livrables step only) */}
          {project.step === 'closing' && project.closing_step === 'livrables' && (() => {
            const b = closingDateBadge(project);
            return (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FontAwesomeIcon icon={faCalendarAlt} size="xs" style={{ color: '#d97706' }} />
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Deadline livrables</span>
                </div>
                {project.livrables_deadline ? (
                  <span style={{ fontSize: '0.76rem', fontWeight: 600, color: b ? b.color : '#92400e' }}>
                    {new Date(project.livrables_deadline).toLocaleDateString('fr-FR')}{b ? ` · ${b.text}` : ''}
                  </span>
                ) : (
                  <span style={{ fontSize: '0.72rem', color: '#bdbdbd' }}>Non définie</span>
                )}
              </div>
            );
          })()}

          {/* Attestation de référence (project without slots) */}
          {project.step === 'closing' && (
            <div style={{ background: localAttestation === true && attestationReceived ? '#f0fdf4' : '#fff7ed', border: `1px solid ${localAttestation === true && attestationReceived ? '#bbf7d0' : '#fed7aa'}`, borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <FontAwesomeIcon icon={faCertificate} size="xs" style={{ color: localAttestation === true && attestationReceived ? '#059669' : '#ea580c' }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: localAttestation === true && attestationReceived ? '#166534' : '#9a3412', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Attestation de référence
                </span>
              </div>
              {localAttestation === null ? (
                <div>
                  <div style={{ fontSize: '0.76rem', color: '#7c2d12', marginBottom: 8 }}>Demander une attestation de référence pour ce projet ?</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => handleSetAttestation(true)} disabled={savingAttestation}
                      style={{ flex: 1, padding: '6px 0', borderRadius: 6, border: 'none', background: '#ea580c', color: '#fff', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', opacity: savingAttestation ? 0.5 : 1 }}>Oui</button>
                    <button onClick={() => handleSetAttestation(false)} disabled={savingAttestation}
                      style={{ flex: 1, padding: '6px 0', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>Non</button>
                  </div>
                </div>
              ) : localAttestation === true ? (
                attestationReceived ? (
                  <span style={{ fontSize: '0.76rem', color: '#166534', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <FontAwesomeIcon icon={faCheckCircle} size="sm" /> Reçue
                  </span>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: '0.76rem', color: '#9a3412', fontWeight: 600 }}>
                        ✓ {localRelanceCount > 0 ? 'Relancé' : (localSentAt ? 'Demandé' : 'À demander')} (voir checklist)
                      </span>
                      <button onClick={() => handleSetAttestation(false)} disabled={savingAttestation}
                        style={{ fontSize: '0.7rem', padding: '3px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', cursor: 'pointer' }}>Annuler la demande</button>
                    </div>
                    {(() => {
                      const sentToday = localSentAt && new Date(localSentAt).toDateString() === new Date().toDateString();
                      const label = sendingAtt
                        ? 'Envoi…'
                        : sentToday
                          ? 'Envoyée aujourd\'hui'
                          : (localSentAt ? 'Envoyer relance' : 'Envoyer demande');
                      return (
                        <div>
                          {sendError && (
                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: '0.7rem', padding: '6px 8px', borderRadius: 6, marginBottom: 6 }}>{sendError}</div>
                          )}
                          {localSentAt && !sentToday && (
                            <div style={{ fontSize: '0.66rem', color: '#9a3412', marginBottom: 6 }}>
                              Dernier envoi : {new Date(localSentAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </div>
                          )}
                          <button onClick={handleSendAttestation} disabled={sendingAtt || sentToday}
                            style={{ width: '100%', padding: '7px 0', borderRadius: 6, border: 'none', fontSize: '0.74rem', fontWeight: 600, cursor: (sendingAtt || sentToday) ? 'default' : 'pointer', background: sentToday ? '#e5e7eb' : '#ea580c', color: sentToday ? '#9ca3af' : '#fff', opacity: sendingAtt ? 0.6 : 1 }}>
                            {label}
                          </button>
                        </div>
                      );
                    })()}
                  </>
                )
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.76rem', color: '#9ca3af' }}>Non demandée</span>
                  <button onClick={() => handleSetAttestation(true)} disabled={savingAttestation}
                    style={{ fontSize: '0.7rem', padding: '3px 10px', borderRadius: 6, border: '1px solid #ea580c', background: '#fff', color: '#ea580c', fontWeight: 600, cursor: 'pointer' }}>Demander</button>
                </div>
              )}
            </div>
          )}

          {/* Délai de relance paiement (facturation / attente de paiement) */}
          {project.step === 'closing'
            && ['facturation', 'attente_paiement'].includes(project.closing_step)
            && (relanceState || project.payment_relance) && (
            <PaymentRelanceCard
              projectId={project.id}
              state={relanceState || project.payment_relance}
              canEdit={!!project.can_set_payment_relance}
              onSaved={(next) => { setRelanceState(next); if (onUpdate) onUpdate(); }}
            />
          )}

          <PaymentRelancePrompt
            isOpen={relancePrompt}
            onClose={() => setRelancePrompt(false)}
            projectId={project.id}
            itemName={project.name}
            currentMode={relanceState?.mode}
            onSaved={(next) => { setRelanceState(next); if (onUpdate) onUpdate(); }}
          />

          {/* Notes */}
          <div style={{ background: '#fafafa', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
            <div onClick={() => setNotesOpen(prev => !prev)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: notesOpen ? 10 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <FontAwesomeIcon icon={notesOpen ? faChevronDown : faChevronRight} size="xs" style={{ color: '#bdbdbd' }} />
                <FontAwesomeIcon icon={faStickyNote} size="xs" style={{ color: '#9e9e9e' }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#616161', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Notes</span>
              </div>
              {localNotes && !notesOpen && <span style={{ fontSize: '0.65rem', color: '#bdbdbd' }}>•••</span>}
            </div>

            {notesOpen && (
              editingNotes ? (
                <div>
                  <textarea value={notesValue} onChange={e => setNotesValue(e.target.value)} rows={3} autoFocus placeholder="Ajouter une note..."
                    style={{ width: '100%', fontSize: '0.78rem', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 6 }}>
                    <button onClick={() => setEditingNotes(false)} disabled={savingNotes}
                      style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.72rem', fontWeight: 500, cursor: 'pointer' }}>Annuler</button>
                    <button onClick={handleSaveNotes} disabled={savingNotes}
                      style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', opacity: savingNotes ? 0.5 : 1 }}>
                      {savingNotes ? <FontAwesomeIcon icon={faSpinner} spin size="xs" /> : 'Enregistrer'}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  {localNotes
                    ? <div style={{ fontSize: '0.78rem', color: '#374151', lineHeight: 1.5, whiteSpace: 'pre-wrap', marginBottom: 6 }}>{localNotes}</div>
                    : <div style={{ fontSize: '0.72rem', color: '#d1d5db', marginBottom: 6 }}>Aucune note</div>}
                  <button onClick={() => { setNotesValue(localNotes); setEditingNotes(true); }}
                    style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.7rem', fontWeight: 500, cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={localNotes ? faEdit : faPlus} size="xs" className="me-1" />{localNotes ? 'Modifier' : 'Ajouter'}
                  </button>
                </div>
              )
            )}
          </div>

          {/* Checklists */}
          <div style={{ background: '#fafafa', borderRadius: 8, padding: '12px 14px' }}>
            <div onClick={() => setChecklistOpen(prev => !prev)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: checklistOpen ? 10 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <FontAwesomeIcon icon={checklistOpen ? faChevronDown : faChevronRight} size="xs" style={{ color: '#bdbdbd' }} />
                <FontAwesomeIcon icon={faClipboardList} size="xs" style={{ color: '#9e9e9e' }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#616161', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Checklists</span>
              </div>
              {checklists.length > 0 && <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#9e9e9e' }}>{doneCount}/{checklists.length}</span>}
            </div>

            {checklistOpen && (
              <>
                {!checklistLoaded ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 0', color: '#9e9e9e', fontSize: '0.78rem' }}>
                    <FontAwesomeIcon icon={faSpinner} spin size="sm" /> Chargement...
                  </div>
                ) : (
                  <>
                    {checklists.length === 0 && <div style={{ fontSize: '0.75rem', color: '#d1d5db', padding: '6px 0' }}>Aucun élément</div>}
                    {Object.entries(grouped).map(([step, items]) => {
                      const stepAccent = STEP_COLORS[step] || '#9e9e9e';
                      const stepDone = items.filter(i => i.is_completed).length;
                      return (
                        <div key={step} style={{ marginBottom: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, paddingBottom: 4, borderBottom: `1.5px solid ${stepAccent}20` }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: stepAccent, flexShrink: 0 }} />
                              <span style={{ fontSize: '0.62rem', fontWeight: 700, color: stepAccent, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{STEP_LABELS[step] || step}</span>
                            </div>
                            <span style={{ fontSize: '0.58rem', fontWeight: 600, color: '#bdbdbd' }}>{stepDone}/{items.length}</span>
                          </div>
                          {items.map(item => <ChecklistRow key={item.id} item={item} onToggle={handleToggle} onDelete={(id) => setDeleteConfirm(id)} />)}
                        </div>
                      );
                    })}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                      <input type="text" value={newLabel} onChange={e => setNewLabel(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleAddChecklist(); }}
                        placeholder={`Ajouter à ${STEP_LABELS[project.step] || project.step}...`}
                        style={addInput} />
                      <button onClick={handleAddChecklist} disabled={!newLabel.trim() || adding}
                        style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: newLabel.trim() ? '#dc2626' : '#e5e7eb', color: newLabel.trim() ? '#fff' : '#bdbdbd', cursor: 'pointer', fontSize: '0.72rem' }}>
                        {adding ? <FontAwesomeIcon icon={faSpinner} size="xs" spin /> : <FontAwesomeIcon icon={faPlus} size="xs" />}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 24px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={() => { onClose(); navigate(`/projects/${project.id}`); }} style={detailBtn}>
            <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" /> Voir le projet
          </button>
        </div>
      </div>
      {deleteConfirm && (
        <div onClick={e => e.stopPropagation()} style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div {...deleteDismiss} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }} />
          <div style={{ position: 'relative', background: '#fff', borderRadius: 12, padding: '24px 28px', width: 380, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h5 style={{ margin: '0 0 8px', fontSize: '0.92rem', fontWeight: 700, color: '#242424' }}>Supprimer cet élément ?</h5>
            <p style={{ fontSize: '0.78rem', color: '#757575', margin: '0 0 18px' }}>Cette action est irréversible.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#424242', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>Annuler</button>
              <button onClick={() => handleDelete(deleteConfirm)} style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes quickModalIn { from { opacity:0; transform:translateY(8px) scale(0.98); } to { opacity:1; transform:translateY(0) scale(1); } }`}</style>
    </div>
  );
};

const ChecklistRow = ({ item, onToggle, onDelete }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #f0f0f0' }}>
    <div onClick={() => onToggle(item.id)} style={{
      width: 16, height: 16, borderRadius: 3, flexShrink: 0, cursor: 'pointer',
      border: item.is_completed ? 'none' : '2px solid #d1d5db',
      background: item.is_completed ? '#dc2626' : '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
    }}>
      {item.is_completed && <svg width="8" height="6" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
    </div>
    <span style={{ flex: 1, fontSize: '0.78rem', color: item.is_completed ? '#bdbdbd' : '#242424', textDecoration: item.is_completed ? 'line-through' : 'none', fontWeight: item.is_completed ? 400 : 500 }}>
      {item.label}
    </span>
    {!(item.step === 'closing' && ['Livrables', 'Réception', 'Facturation', 'Encaissé', 'Attestation de référence'].includes(item.label)) && (
      <button onClick={() => onDelete(item.id)} style={{ background: 'none', border: 'none', color: '#e0e0e0', cursor: 'pointer', padding: 2, transition: 'color 0.12s' }}
        onMouseEnter={e => e.currentTarget.style.color = '#d13438'} onMouseLeave={e => e.currentTarget.style.color = '#e0e0e0'}>
        <FontAwesomeIcon icon={faTrash} size="xs" />
      </button>
    )}
  </div>
);

const overlay = { position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)' };
const modal = { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 560, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.18)', overflow: 'hidden', animation: 'quickModalIn 0.2s ease' };
const closeBtn = { background: 'none', border: 'none', color: '#bdbdbd', cursor: 'pointer', padding: 6, borderRadius: 6 };
const infoCard = { background: '#fafafa', borderRadius: 8, padding: '12px 14px' };
const infoCardLabel = { fontSize: '0.6rem', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 };
const counterChip = { display: 'flex', alignItems: 'center', gap: 8, flex: 1, background: '#fafafa', borderRadius: 8, padding: '10px 14px', transition: 'background 0.12s' };
const addInput = { flex: 1, fontSize: '0.75rem', padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none', background: '#fff' };
const detailBtn = { fontSize: '0.78rem', fontWeight: 600, padding: '8px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 };

export default ProjectQuickModal;
